import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { type AxiosInstance } from "axios";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MIN_TAP_TARGET, MOBILE_BREAKPOINT } from "@/styles/responsive";
import { getCorporationListCandidates } from "../api/corporationList";
import {
  fields,
  labels,
  type Corporation,
  type InputRow,
  type MatchedRow,
} from "./types";
import { errorMessage } from "./messages";

interface Props {
  input: InputRow | null;
  result: MatchedRow | null;
  api: AxiosInstance;
  busy: boolean;
  matchError: string;
  close: () => void;
  save: (change: Partial<InputRow>) => Promise<boolean>;
}
export const ResolutionDialog = ({
  input,
  result,
  api,
  busy,
  matchError,
  close,
  save,
}: Props): JSX.Element => {
  const isMobile = useIsMobile();
  const tapSx = isMobile ? { minHeight: MIN_TAP_TARGET } : undefined;
  const apiRef = useRef(api);
  apiRef.current = api;
  const abort = useRef<AbortController>();
  const [draft, setDraft] = useState<InputRow | null>(input);
  const [candidates, setCandidates] = useState<Corporation[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    setDraft(input);
    setLoading(false);
    setCandidates(result?.candidates ?? []);
    setCursor(null);
    setError("");
    return () => {
      abort.current?.abort();
    };
  }, [input, result]);
  const loadCandidates = async (after: string): Promise<void> => {
    if (!draft) return;
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    setLoading(true);
    setError("");
    try {
      const data = await getCorporationListCandidates(
        apiRef.current,
        draft,
        after,
        controller.signal
      );
      if (controller.signal.aborted) return;
      setCandidates((previous) =>
        after ? [...previous, ...data.candidates] : data.candidates
      );
      setCursor(data.nextCursor);
    } catch (error) {
      if (!controller.signal.aborted) setError(errorMessage(error));
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };
  const select = async (corporation: Corporation): Promise<void> => {
    const matched = await save({
      ...draft,
      corporateNumber: "",
      corporateIdentificationNumber: corporation.corporateIdentificationNumber,
      resolutionSource: "CANDIDATE",
      explicitlyExcluded: false,
      cellErrors: {},
    });
    if (matched) close();
  };
  return (
    // 誤タップで入力中の修正を失わないよう、背景タップ・Escでは閉じない（レスポンシブ基盤ガイド）
    <Dialog open={Boolean(input)} fullScreen={isMobile} fullWidth maxWidth="md">
      <DialogTitle>レコード{input?.rowNumber}の法人を確認</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {(error || matchError) && (
            <Alert severity="error">{error || matchError}</Alert>
          )}
          <Typography>
            候補から1法人を選ぶか、入力内容を修正してください。番号は国税庁データと照合します。
          </Typography>
          {candidates.map((corporation) => (
            <Stack
              key={corporation.corporateNumber}
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              sx={{ borderBottom: 1, borderColor: "divider", pb: 1 }}
            >
              <div>
                <Typography fontWeight={600}>
                  {corporation.companyName}
                </Typography>
                <Typography variant="body2">
                  {corporation.prefectureName}
                  {corporation.cityName}
                  {corporation.streetNumber}
                </Typography>
                <Typography variant="caption">
                  会社法人等番号 {corporation.corporateIdentificationNumber}
                </Typography>
              </div>
              <Button
                disabled={busy}
                sx={tapSx}
                onClick={() => {
                  void select(corporation);
                }}
                aria-label={`${corporation.companyName} ${corporation.corporateIdentificationNumber}を選択`}
              >
                選択
              </Button>
            </Stack>
          ))}
          {draft?.companyName && draft.prefectureName && draft.cityName && (
            <Button
              disabled={loading || busy}
              sx={tapSx}
              onClick={() => {
                void loadCandidates(cursor ?? "");
              }}
            >
              {loading
                ? "候補を読込中"
                : cursor
                ? "次の候補を表示"
                : "候補を検索・再表示"}
            </Button>
          )}
          <Typography variant="subtitle2">
            番号の手入力・入力内容の修正
          </Typography>
          <Stack
            direction={{ xs: "column", [MOBILE_BREAKPOINT]: "row" }}
            flexWrap="wrap"
            gap={2}
          >
            {draft &&
              fields.map((field) => (
                <TextField
                  key={field}
                  label={labels[field]}
                  size="small"
                  value={draft[field]}
                  disabled={busy}
                  inputProps={
                    field === "corporateNumber" ||
                    field === "corporateIdentificationNumber"
                      ? { inputMode: "numeric", pattern: "\\d*" }
                      : undefined
                  }
                  onChange={(event) => {
                    abort.current?.abort();
                    setLoading(false);
                    setCandidates([]);
                    setCursor(null);
                    setError("");
                    setDraft({ ...draft, [field]: event.target.value });
                  }}
                  sx={{
                    flex: { xs: "0 0 auto", [MOBILE_BREAKPOINT]: "1 1 240px" },
                  }}
                />
              ))}
          </Stack>
          <Typography variant="caption">
            法人番号と会社法人等番号の両方を入力する場合は、同じ法人の番号を指定してください。
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
        <Button onClick={close} disabled={busy} sx={tapSx}>
          閉じる
        </Button>
        {result && (
          <Button
            disabled={busy}
            sx={tapSx}
            onClick={() => {
              void save({
                explicitlyExcluded: !result.explicitlyExcluded,
              }).then((matched) => {
                if (matched) close();
              });
            }}
          >
            {result.explicitlyExcluded ? "除外を解除" : "この行を除外する"}
          </Button>
        )}
        <Button
          variant="contained"
          disabled={busy || !draft}
          sx={tapSx}
          onClick={() => {
            if (draft)
              void save({
                ...draft,
                resolutionSource: "MANUAL",
                explicitlyExcluded: false,
                cellErrors: {},
              }).then((matched) => {
                if (matched) close();
              });
          }}
        >
          入力内容で照合
        </Button>
      </DialogActions>
    </Dialog>
  );
};
